import { Component, type ReactNode, type ErrorInfo } from 'react'
import { getTranslations, detectLocale, type Locale } from './i18n'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  locale?: Locale
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error?: Error
}

/**
 * Error Boundary for Radar Components
 * Catches errors in Canvas and Portal components to prevent app crashes
 *
 * Phase 4.1: Error Boundaries
 */
export class RadarErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Radar Error:', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  handleReload = () => {
    window.location.reload()
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    const t = getTranslations(this.props.locale ?? detectLocale())

    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <div className="p-6 text-center rounded-xl border border-red-200 bg-red-50/50">
          <div className="mb-4">
            <svg
              className="w-12 h-12 mx-auto text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-red-800">
            {t.errors.title}
          </h3>
          <p className="mb-4 text-sm text-red-600">
            {t.errors.description}
          </p>
          <div className="flex justify-center gap-2">
            <button
              onClick={this.handleRetry}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
            >
              {t.buttons.retry}
            </button>
            <button
              onClick={this.handleReload}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition"
            >
              {t.buttons.reload}
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Hook version for functional components
 */
export function useRadarErrorHandler() {
  return {
    logError: (error: Error, context?: string) => {
      console.error(`[Radar Error${context ? ` - ${context}` : ''}]:`, error)
    },
  }
}
